import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaTextarea } from "@/shared/components/forms/ColocaTextarea";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { REJECT_REASON_MAX_LENGTH, REJECT_REASON_MIN_LENGTH } from "../../constants/payoutBatches.constants";

export interface RejectBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
  isPending: boolean;
  errorMessage: string | null;
}

/** Sends the batch back to its maker with the reason they will read; nothing is paid or cancelled. */
export function RejectBatchDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  errorMessage,
}: RejectBatchDialogProps) {
  const { t } = useTranslations("payoutBatches");
  const [reason, setReason] = useState("");
  const tooShort = reason.trim().length < REJECT_REASON_MIN_LENGTH;

  const handleOpenChange = (next: boolean) => {
    if (!next && isPending) return;
    if (!next) setReason("");
    onOpenChange(next);
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="sm"
      title={t("reject_title")}
      description={t("reject_description")}
      showCloseButton={!isPending}
      footer={
        <>
          <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={isPending} />}>
            {t("action_close")}
          </ColocaDialogClose>
          <ColocaButton
            variant="primary"
            loading={isPending}
            disabled={tooShort}
            onClick={() => void onConfirm(reason.trim())}
          >
            {t("reject_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-2">
        <label htmlFor="payout-batch-reject-reason" className="text-sm font-medium text-foreground">
          {t("reject_reason_label")}
        </label>
        <ColocaTextarea
          id="payout-batch-reject-reason"
          value={reason}
          onValueChange={setReason}
          placeholder={t("reject_reason_placeholder")}
          maxLength={REJECT_REASON_MAX_LENGTH}
          rows={3}
          disabled={isPending}
        />
        <p className="text-xs text-muted-foreground">
          {t("reject_reason_hint", { count: REJECT_REASON_MIN_LENGTH })}
        </p>
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      </div>
    </ColocaDialog>
  );
}
